import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { 
  Bell, 
  BellRing, 
  BellOff,
  ChevronLeft,
  Heart,
  TrendingUp,
  Radio,
  Loader2,
  Check,
  X,
  Smartphone,
  Clock,
  Trophy
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// VAPID public key for push notifications (should match backend)
const VAPID_PUBLIC_KEY = 'BEl62iUYgUivxIkv69yViEuiBIa-Ib9-SkvMeAtA3LFgDzkrxZJjSgSnfckjBJuBkr3qBUYIHBQFLXYp5Nksh8U';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function Notifications() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);
  const [preferences, setPreferences] = useState({
    favorite_team_alerts: true,
    high_confidence_alerts: true,
    live_score_alerts: false,
    game_start_alerts: true,
    prediction_result_alerts: true,
    daily_summary_alerts: false,
    min_probability_threshold: 70
  });

  useEffect(() => {
    // Check if push notifications are supported and register service worker
    if ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window) {
      setPushSupported(true);
      registerServiceWorker();
    }
    
    if (isAuthenticated) {
      fetchPreferences();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const registerServiceWorker = async () => {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration);
      setSwRegistration(registration);
      
      // Check if already subscribed
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        setPushEnabled(true);
      }
    } catch (error) {
      console.error('Service Worker registration failed:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPreferences(prev => ({ ...prev, ...data.preferences }));
        setPushEnabled(data.push_enabled);
      }
    } catch (err) {
      console.error('Failed to fetch preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include',
        body: JSON.stringify(preferences)
      });
      
      if (response.ok) {
        toast.success('Notification preferences saved');
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const requestPushPermission = async () => {
    if (!pushSupported || !swRegistration) {
      toast.error('Push notifications not supported in this browser');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Subscribe to push notifications
        const subscription = await swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
        });

        // Send subscription to backend
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_URL}/api/notifications/subscribe`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          credentials: 'include',
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            keys: {
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))),
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh'))))
            }
          })
        });
        
        if (response.ok) {
          setPushEnabled(true);
          toast.success('Push notifications enabled!');
        } else {
          throw new Error('Failed to save subscription');
        }
      } else {
        toast.error('Push notification permission denied');
      }
    } catch (err) {
      console.error('Push subscription error:', err);
      toast.error('Failed to enable push notifications');
    }
  };

  const disablePush = async () => {
    try {
      if (swRegistration) {
        const subscription = await swRegistration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }
      }

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/unsubscribe`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setPushEnabled(false);
        toast.success('Push notifications disabled');
      }
    } catch (err) {
      toast.error('Failed to disable push notifications');
    }
  };

  const testNotification = () => {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('SportsAnalysisAB Test', {
        body: 'Push notifications are working!',
        icon: '/logo192.png'
      });
      toast.success('Test notification sent!');
    } else {
      toast.error('Push notifications not enabled');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <BellOff className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="font-display text-xl font-bold uppercase mb-2">Sign In Required</h2>
            <p className="text-muted-foreground mb-4">Sign in to manage your notification preferences</p>
            <Button onClick={() => navigate('/login')} className="rounded-sm">
              Sign In
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex items-center gap-2 mb-4">
              <Button 
                variant="ghost" 
                size="sm" 
                className="rounded-sm"
                onClick={() => navigate('/board')}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back to Board
              </Button>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-orange-500/10 rounded-sm flex items-center justify-center">
                <Bell className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h1 className="font-display text-3xl font-bold uppercase tracking-tight">
                  Notifications
                </h1>
                <p className="text-muted-foreground">
                  Manage your alert preferences
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
          {/* Push Notification Status */}
          <div className="bg-card border border-border rounded-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/10 rounded-sm flex items-center justify-center">
                  <Smartphone className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight">
                    Push Notifications
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Receive browser notifications for game alerts
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Badge variant={pushEnabled ? 'default' : 'secondary'}>
                  {pushEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
                {pushEnabled ? (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-sm"
                    onClick={disablePush}
                  >
                    Disable
                  </Button>
                ) : (
                  <Button 
                    size="sm" 
                    className="rounded-sm"
                    onClick={requestPushPermission}
                    disabled={!pushSupported}
                  >
                    <BellRing className="w-4 h-4 mr-2" />
                    Enable
                  </Button>
                )}
              </div>
            </div>
            {!pushSupported && (
              <p className="text-xs text-yellow-500">
                Push notifications are not supported in this browser
              </p>
            )}
            {pushEnabled && (
              <div className="mt-4 pt-4 border-t border-border">
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="rounded-sm"
                  onClick={testNotification}
                  data-testid="test-notification-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Send Test Notification
                </Button>
              </div>
            )}
          </div>

          {/* Notification Preferences */}
          <div className="bg-card border border-border rounded-sm p-6 space-y-6">
            <h3 className="font-display text-lg font-bold uppercase tracking-tight mb-4">
              Alert Preferences
            </h3>

            {/* Favorite Team Alerts */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Heart className="w-5 h-5 text-red-400" />
                <div>
                  <p className="font-medium">Favorite Team Alerts</p>
                  <p className="text-sm text-muted-foreground">
                    Get notified when your favorite teams play
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.favorite_team_alerts}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, favorite_team_alerts: checked })
                }
                data-testid="favorite-alerts-toggle"
              />
            </div>

            {/* High Confidence Alerts */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <TrendingUp className="w-5 h-5 text-green-400" />
                <div>
                  <p className="font-medium">High Confidence Alerts</p>
                  <p className="text-sm text-muted-foreground">
                    Get notified for high confidence predictions
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.high_confidence_alerts}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, high_confidence_alerts: checked })
                }
                data-testid="confidence-alerts-toggle"
              />
            </div>

            {/* Live Score Alerts */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Radio className="w-5 h-5 text-red-400" />
                <div>
                  <p className="font-medium">Live Score Updates</p>
                  <p className="text-sm text-muted-foreground">
                    Get notified during live games (score changes)
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.live_score_alerts}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, live_score_alerts: checked })
                }
                data-testid="live-alerts-toggle"
              />
            </div>

            {/* Game Start Alerts */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Clock className="w-5 h-5 text-blue-400" />
                <div>
                  <p className="font-medium">Game Start Reminders</p>
                  <p className="text-sm text-muted-foreground">
                    Get notified 30 minutes before games start
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.game_start_alerts}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, game_start_alerts: checked })
                }
                data-testid="game-start-alerts-toggle"
              />
            </div>

            {/* Prediction Result Alerts */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <div>
                  <p className="font-medium">Prediction Results</p>
                  <p className="text-sm text-muted-foreground">
                    Get notified when your predictions are resolved
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.prediction_result_alerts}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, prediction_result_alerts: checked })
                }
                data-testid="prediction-result-alerts-toggle"
              />
            </div>

            {/* Daily Summary Alerts */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Bell className="w-5 h-5 text-purple-400" />
                <div>
                  <p className="font-medium">Daily Summary</p>
                  <p className="text-sm text-muted-foreground">
                    Receive a daily summary of predictions and results
                  </p>
                </div>
              </div>
              <Switch
                checked={preferences.daily_summary_alerts}
                onCheckedChange={(checked) => 
                  setPreferences({ ...preferences, daily_summary_alerts: checked })
                }
                data-testid="daily-summary-alerts-toggle"
              />
            </div>

            {/* Probability Threshold */}
            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="font-medium">Minimum Probability Threshold</p>
                  <p className="text-sm text-muted-foreground">
                    Only alert for predictions above this confidence
                  </p>
                </div>
                <span className="font-mono text-lg font-bold text-green-400">
                  {preferences.min_probability_threshold}%
                </span>
              </div>
              <Slider
                value={[preferences.min_probability_threshold]}
                onValueChange={(value) => 
                  setPreferences({ ...preferences, min_probability_threshold: value[0] })
                }
                min={50}
                max={90}
                step={5}
                className="w-full"
                data-testid="probability-slider"
              />
              <div className="flex justify-between text-xs text-muted-foreground mt-2">
                <span>50%</span>
                <span>70%</span>
                <span>90%</span>
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              onClick={savePreferences}
              disabled={saving}
              className="rounded-sm"
              data-testid="save-preferences-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Save Preferences
            </Button>
          </div>

          {/* Info */}
          <div className="bg-blue-500/5 border border-blue-500/20 rounded-sm p-6">
            <div className="flex gap-3">
              <Bell className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display font-bold uppercase tracking-tight text-blue-400 mb-2">
                  About Game Alerts
                </h3>
                <p className="text-sm text-muted-foreground">
                  Alerts are sent based on your preferences when notable games are about to start, 
                  when live games have significant score changes, or when high-confidence matchups 
                  are available. You can adjust these settings at any time.
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
